(() => {
  const ROOT_SELECTOR = '[data-funny-product-page-v2]';

  class FunnyProductPageV2 {
    constructor(root) {
      this.root = root;
      this.currency = root.dataset.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
      this.locale = root.dataset.locale || document.documentElement.lang || navigator.language || 'en-US';
      this.variants = this.readVariants();

      this.currentVariant =
        this.variants.find(v => String(v.id) === String(this.root.querySelector('[data-variant-id]')?.value)) ||
        this.variants.find(v => v.available) ||
        this.variants[0] ||
        null;

      this.viewingTimer = null;
      this.lastViewerCount = null;
      this.stickyObserver = null;
      this.stickyCartDrawerOpen = false;
      this.stickyCartDrawerGraceUntil = 0;
      this.stickyVisibilityCleanup = null;

      this.bindGallery();
      this.bindAccordions();
      this.bindVariants();
      this.bindForm();
      this.bindStickyBar();
      this.bindReviewScroll();
      this.bindViewingCounter();

      if (this.currentVariant) {
        this.applyVariant(this.currentVariant, { updateMedia: false });
      }
    }

    readVariants() {
      const node = this.root.querySelector('[data-product-json-v2]');
      if (!node) return [];

      try {
        return JSON.parse(node.textContent || '[]');
      } catch (error) {
        console.error('[FunnyProductPageV2] Product JSON could not be parsed:', error);
        return [];
      }
    }

    bindGallery() {
      this.root.querySelectorAll('[data-media-target]').forEach(button => {
        button.addEventListener('click', () => this.showMedia(button.dataset.mediaTarget));
      });

      const thumbs = this.root.querySelector('[data-thumbs]');
      const prev = this.root.querySelector('[data-thumb-prev]');
      const next = this.root.querySelector('[data-thumb-next]');

      prev?.addEventListener('click', () => thumbs?.scrollBy({ left: -220, behavior: 'smooth' }));
      next?.addEventListener('click', () => thumbs?.scrollBy({ left: 220, behavior: 'smooth' }));

      this.bindGallerySwipe();
    }

    bindGallerySwipe() {
      const mainWrap = this.root.querySelector('.fpp-main-media-wrap');
      if (!mainWrap) return;

      let startX = 0;
      let startY = 0;

      mainWrap.addEventListener('touchstart', event => {
        if (event.touches.length !== 1) return;
        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
      }, { passive: true });

      mainWrap.addEventListener('touchend', event => {
        if (!window.matchMedia('(max-width: 989px)').matches) return;
        if (!event.changedTouches.length) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;
        const horizontalDistance = Math.abs(deltaX);
        const verticalDistance = Math.abs(deltaY);

        if (horizontalDistance < 42) return;
        if (horizontalDistance <= verticalDistance * 1.15) return;

        this.moveGalleryMedia(deltaX < 0 ? 1 : -1);
      }, { passive: true });
    }

    moveGalleryMedia(direction) {
      const panels = [...this.root.querySelectorAll('[data-media-panel]')];
      if (panels.length < 2) return;

      let currentIndex = panels.findIndex(panel =>
        panel.classList.contains('is-active') && !panel.hidden
      );

      if (currentIndex < 0) currentIndex = 0;

      const nextIndex = (currentIndex + direction + panels.length) % panels.length;
      const nextPanel = panels[nextIndex];
      const nextId = nextPanel?.dataset.mediaPanel;
      if (!nextId) return;

      this.showMedia(nextId);

      const thumbs = this.root.querySelector('[data-thumbs]');
      const activeThumb = this.root.querySelector(`[data-media-target="${nextId}"]`);

      if (thumbs && activeThumb) {
        const targetLeft =
          activeThumb.offsetLeft -
          (thumbs.clientWidth / 2) +
          (activeThumb.offsetWidth / 2);

        thumbs.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: 'smooth',
        });
      }
    }

    showMedia(id) {
      if (!id) return;

      this.root.querySelectorAll('[data-media-panel]').forEach(panel => {
        const active = String(panel.dataset.mediaPanel) === String(id);
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      this.root.querySelectorAll('[data-media-target]').forEach(button => {
        const active = String(button.dataset.mediaTarget) === String(id);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    bindAccordions() {
      this.root.querySelectorAll('[data-accordion]').forEach(item => {
        const trigger = item.querySelector('.fpp-accordion-trigger');
        const panel = item.querySelector('.fpp-accordion-panel');
        const chevron = item.querySelector('.fpp-chevron');

        if (!trigger || !panel) return;

        trigger.addEventListener('click', () => {
          const open = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.hidden = open;
          item.classList.toggle('is-open', !open);
          if (chevron) chevron.textContent = open ? '⌄' : '⌃';
        });
      });
    }

    bindVariants() {
      this.root.querySelectorAll('[data-variant-card]').forEach(card => {
        const button = card.querySelector('[data-variant-select]');
        const variantId = String(card.dataset.variantId || '');

        button?.addEventListener('click', event => {
          event.preventDefault();

          const variant = this.variants.find(
            item => String(item.id) === variantId
          );

          if (!variant?.available) return;
          this.applyVariant(variant);
        });
      });
    }

    applyVariant(variant, options = {}) {
      if (!variant) return;

      this.currentVariant = variant;

      const idField = this.root.querySelector('[data-variant-id]');
      if (idField) idField.value = variant.id;

      this.root.querySelectorAll('[data-variant-card]').forEach(card => {
        const selected = String(card.dataset.variantId) === String(variant.id);
        card.classList.toggle('is-selected', selected);

        const button = card.querySelector('[data-variant-select]');
        const badge = card.querySelector('[data-variant-badge]');

        button?.setAttribute('aria-pressed', selected ? 'true' : 'false');
        if (badge) badge.hidden = !selected;
      });

      const price = Number(variant.price || 0);
      const singlePrice = this.root.querySelector('[data-single-price]');
      if (singlePrice) singlePrice.textContent = this.formatMoney(price);

      this.updateStockBadge(variant);
      this.updateCountryCard(variant);
      this.updatePurchaseButtons();

      if (options.updateMedia !== false && variant.featured_media?.id) {
        this.showMedia(variant.featured_media.id);
      }
    }

    updateStockBadge(variant) {
      const badge = this.root.querySelector('[data-stock-badge]');
      const textNode = this.root.querySelector('[data-stock-text]');
      if (!badge || !textNode || !variant) return;

      const stockTemplate = this.root.dataset.stockTemplate || 'Only [count] left in stock!';
      const inStockText = this.root.dataset.inStockText || 'In stock and ready to order';
      const soldOutText = this.root.dataset.stockSoldoutText || 'Sold out';
      const qty = Number(variant.inventory_quantity || 0);
      const tracked = Boolean(variant.inventory_management);

      if (!variant.available) {
        textNode.textContent = soldOutText;
        badge.classList.add('is-sold-out');
        return;
      }

      badge.classList.remove('is-sold-out');

      if (tracked && qty > 0) {
        textNode.textContent = stockTemplate.includes('[count]')
          ? stockTemplate.replaceAll('[count]', String(qty))
          : `${stockTemplate} ${qty}`;
      } else {
        textNode.textContent = inStockText;
      }
    }

    updateCountryCard(variant) {
      const card = this.root.querySelector('[data-country-card]');
      if (!card) return;
      card.hidden = !variant?.available;
    }

    updatePurchaseButtons() {
      const variant = this.currentVariant;
      const available = Boolean(variant?.available);
      const addButton = this.root.querySelector('[data-add-button]');
      const addButtonText = this.root.querySelector('[data-add-button-text]');
      const stickyButton = this.root.querySelector('[data-sticky-button]');
      const stickyButtonText = this.root.querySelector('[data-sticky-button-text]');
      const stickyPrice = this.root.querySelector('[data-sticky-price]');
      const singleItem = this.root.querySelector('[data-sticky-selection]');

      const addText =
        this.root.dataset.stickyAddText ||
        addButtonText?.dataset.addText ||
        'Add to cart';

      const soldText =
        this.root.dataset.stickySoldoutText ||
        addButtonText?.dataset.soldText ||
        'Sold out';

      if (addButton && addButton.getAttribute('aria-busy') !== 'true') {
        addButton.disabled = !available;
      }

      if (addButtonText && addButton?.getAttribute('aria-busy') !== 'true') {
        addButtonText.textContent = available ? addText : soldText;
      }

      if (stickyButton) stickyButton.disabled = !available;
      if (stickyButtonText) stickyButtonText.textContent = available ? addText : soldText;

      if (stickyPrice) {
        stickyPrice.textContent = this.formatMoney(Number(variant?.price || 0));
      }

      if (singleItem) {
        singleItem.textContent = this.root.dataset.singleItemText || '1 item';
      }
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${this.currency}`;
      }
    }

    isCartDrawerOpen() {
      const selectors = [
        'cart-drawer',
        'modal-cart',
        '#CartDrawer',
        '#cart-drawer',
        '[data-cart-drawer]',
        '[data-modal-cart]',
        '.cart-drawer',
        '.modal-cart',
        '[x-show*="cart"]',
        '[x-show*="Cart"]',
      ];

      const candidates = [...document.querySelectorAll(selectors.join(','))];

      return candidates.some(element => {
        if (!element || element === this.root) return false;
        if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

        const classList = element.classList;
        const explicitlyOpen =
          element.hasAttribute('open') ||
          element.getAttribute('aria-hidden') === 'false' ||
          element.getAttribute('data-state') === 'open' ||
          classList.contains('is-open') ||
          classList.contains('open') ||
          classList.contains('active') ||
          classList.contains('is-active');

        if (explicitlyOpen) return true;

        const openChild = element.querySelector(
          'dialog[open], [aria-modal="true"]:not([aria-hidden="true"]), [data-state="open"], .is-open, .is-active'
        );

        if (openChild) return true;

        const xShow = (
          element.getAttribute('x-show') ||
          element.getAttribute('x-bind:class') ||
          ''
        ).toLowerCase();

        if (!xShow.includes('cart')) return false;

        const style = window.getComputedStyle(element);
        if (
          style.display === 'none' ||
          style.visibility === 'hidden' ||
          Number.parseFloat(style.opacity || '1') === 0
        ) {
          return false;
        }

        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
    }

    bindStickyBar() {
      const bar = this.root.querySelector('[data-sticky-bar]');
      const button = this.root.querySelector('[data-sticky-button]');
      if (!bar || !button) return;

      button.addEventListener('click', event => {
        event.preventDefault();

        if (!this.currentVariant?.available) return;

        const form = this.root.querySelector('[data-product-form]');
        if (!form) return;

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });

      const footerCandidates = [
        ...document.querySelectorAll(
          'footer, #shopify-section-footer, .shopify-section-group-footer-group, [data-section-type="footer"]'
        ),
      ];

      const footer = footerCandidates.length
        ? footerCandidates[footerCandidates.length - 1]
        : null;

      let ticking = false;

      const updateStickyVisibility = () => {
        ticking = false;

        const rootRect = this.root.getBoundingClientRect();
        const rootTop = window.scrollY + rootRect.top;
        const scrollThreshold = rootTop + Math.min(180, window.innerHeight * 0.18);
        const hasScrolledEnough = window.scrollY > scrollThreshold;

        let footerReached = false;

        if (footer) {
          const footerRect = footer.getBoundingClientRect();
          footerReached =
            footerRect.top <= window.innerHeight &&
            footerRect.bottom >= 0;
        }

        const detectedCartDrawerOpen = this.isCartDrawerOpen();

        if (detectedCartDrawerOpen) {
          this.stickyCartDrawerOpen = true;
        } else if (Date.now() > this.stickyCartDrawerGraceUntil) {
          this.stickyCartDrawerOpen = false;
        }

        const shouldShow =
          hasScrolledEnough &&
          !footerReached &&
          !this.stickyCartDrawerOpen;

        bar.classList.toggle('is-visible', shouldShow);
        bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      };

      const scheduleStickyVisibility = () => {
        if (ticking) return;

        ticking = true;
        window.requestAnimationFrame(updateStickyVisibility);
      };

      const forceCartDrawerOpen = () => {
        this.stickyCartDrawerOpen = true;
        this.stickyCartDrawerGraceUntil = Date.now() + 900;
        scheduleStickyVisibility();

        // Give Alpine / the theme enough time to update x-show / drawer state.
        window.setTimeout(scheduleStickyVisibility, 60);
        window.setTimeout(scheduleStickyVisibility, 320);
      };

      const recheckCartDrawer = () => {
        window.setTimeout(() => {
          this.stickyCartDrawerGraceUntil = 0;
          this.stickyCartDrawerOpen = this.isCartDrawerOpen();
          scheduleStickyVisibility();
        }, 80);
      };

      const handleDocumentClick = event => {
        const trigger = event.target?.closest?.('button, a, [role="button"]');
        if (!trigger) return;

        const attributes = trigger
          .getAttributeNames()
          .map(name => `${name}=${trigger.getAttribute(name) || ''}`)
          .join(' ')
          .toLowerCase();

        const href = (trigger.getAttribute('href') || '').toLowerCase();
        const ariaControls = (trigger.getAttribute('aria-controls') || '').toLowerCase();

        const looksLikeCartOpen =
          (
            attributes.includes('cart') &&
            (
              attributes.includes('modal') ||
              attributes.includes('drawer') ||
              attributes.includes('open')
            )
          ) ||
          ariaControls.includes('cart') ||
          href === '/cart' ||
          href.endsWith('/cart');

        if (looksLikeCartOpen) {
          forceCartDrawerOpen();
          return;
        }

        const cartContainer = trigger.closest(
          'cart-drawer, modal-cart, #CartDrawer, #cart-drawer, [data-cart-drawer], [data-modal-cart], .cart-drawer, .modal-cart, [x-show*="cart"], [x-show*="Cart"]'
        );

        const looksLikeClose =
          attributes.includes('close') ||
          (trigger.getAttribute('aria-label') || '').toLowerCase().includes('close') ||
          (trigger.getAttribute('title') || '').toLowerCase().includes('close');

        if (cartContainer && looksLikeClose) {
          this.stickyCartDrawerOpen = false;
          recheckCartDrawer();
        }
      };

      const handleEscape = event => {
        if (event.key !== 'Escape') return;
        if (!this.stickyCartDrawerOpen) return;

        this.stickyCartDrawerOpen = false;
        recheckCartDrawer();
      };

      const handleAfterAdd = () => {
        forceCartDrawerOpen();
      };

      const handlePossibleClose = () => {
        recheckCartDrawer();
      };

      window.addEventListener('scroll', scheduleStickyVisibility, { passive: true });
      window.addEventListener('resize', scheduleStickyVisibility, { passive: true });

      window.addEventListener('shapes:modalcart:afteradditem', handleAfterAdd);
      document.body?.addEventListener('shapes:modalcart:afteradditem', handleAfterAdd);
      window.addEventListener('shapes:cart:afteradditem', handleAfterAdd);

      window.addEventListener('shapes:modalcart:close', handlePossibleClose);
      window.addEventListener('shapes:modalcart:closed', handlePossibleClose);
      window.addEventListener('shapes:cart:close', handlePossibleClose);
      window.addEventListener('shapes:cart:closed', handlePossibleClose);

      document.addEventListener('click', handleDocumentClick, true);
      document.addEventListener('keydown', handleEscape, true);

      this.stickyObserver?.disconnect();
      this.stickyObserver = new MutationObserver(scheduleStickyVisibility);
      this.stickyObserver.observe(document.body || document.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          'class',
          'style',
          'hidden',
          'open',
          'aria-hidden',
          'data-state',
        ],
      });

      this.stickyVisibilityCleanup = () => {
        window.removeEventListener('scroll', scheduleStickyVisibility);
        window.removeEventListener('resize', scheduleStickyVisibility);

        window.removeEventListener('shapes:modalcart:afteradditem', handleAfterAdd);
        document.body?.removeEventListener('shapes:modalcart:afteradditem', handleAfterAdd);
        window.removeEventListener('shapes:cart:afteradditem', handleAfterAdd);

        window.removeEventListener('shapes:modalcart:close', handlePossibleClose);
        window.removeEventListener('shapes:modalcart:closed', handlePossibleClose);
        window.removeEventListener('shapes:cart:close', handlePossibleClose);
        window.removeEventListener('shapes:cart:closed', handlePossibleClose);

        document.removeEventListener('click', handleDocumentClick, true);
        document.removeEventListener('keydown', handleEscape, true);

        this.stickyObserver?.disconnect();
        this.stickyObserver = null;
      };

      this.stickyCartDrawerOpen = this.isCartDrawerOpen();
      updateStickyVisibility();
    }

    bindReviewScroll() {
      const link = this.root.querySelector('[data-review-link]');
      if (!link) return;

      link.addEventListener('click', event => {
        const href = link.getAttribute('href') || '';
        const id = href.startsWith('#') ? href.slice(1) : href;
        if (!id) return;

        const target = document.getElementById(id);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });

        if (history.replaceState) {
          history.replaceState(null, '', `#${id}`);
        }
      });
    }

    bindViewingCounter() {
      const pill = this.root.querySelector('[data-viewing-pill]');
      const textNode = this.root.querySelector('[data-viewing-text]');
      if (!pill || !textNode) return;

      this.renderViewerCount(textNode);
      this.scheduleViewerUpdate(textNode);
    }

    renderViewerCount(textNode) {
      const rawMin = parseInt(this.root.dataset.viewingMin || '17', 10);
      const rawMax = parseInt(this.root.dataset.viewingMax || '28', 10);
      const min = Math.max(1, Math.min(rawMin, rawMax));
      const max = Math.max(min, Math.max(rawMin, rawMax));

      let count = Math.floor(Math.random() * (max - min + 1)) + min;

      if (max > min && count === this.lastViewerCount) {
        count = count === max ? min : count + 1;
      }

      this.lastViewerCount = count;

      const template = this.root.dataset.viewingTemplate || '21 people are viewing this now';

      textNode.textContent = /\d[\d.,]*/.test(template)
        ? template.replace(/\d[\d.,]*/, String(count))
        : `${count} ${template}`;
    }

    scheduleViewerUpdate(textNode) {
      clearTimeout(this.viewingTimer);

      const delay = 7000 + Math.floor(Math.random() * 1001);

      this.viewingTimer = window.setTimeout(() => {
        this.renderViewerCount(textNode);
        this.scheduleViewerUpdate(textNode);
      }, delay);
    }

    bindForm() {
      const form = this.root.querySelector('[data-product-form]');
      if (!form) return;

      form.addEventListener('submit', async event => {
        event.preventDefault();

        const variant = this.currentVariant;
        const button = this.root.querySelector('[data-add-button]');
        const buttonText = this.root.querySelector('[data-add-button-text]');

        if (!variant?.available || !button || button.disabled) return;

        const normalText =
          this.root.dataset.stickyAddText ||
          buttonText?.dataset.addText ||
          'Add to cart';

        const addingText = this.root.dataset.addingText || 'Adding...';
        const addedText = this.root.dataset.addedText || 'Added!';

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        if (buttonText) buttonText.textContent = addingText;

        let addedSuccessfully = false;

        try {
          const rootUrl = window.Shopify?.routes?.root || '/';

          const payload = {
            items: [{
              id: Number(variant.id),
              quantity: 1,
            }],
            sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
            sections_url: window.location.pathname,
          };

          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
            body: JSON.stringify(payload),
          });

          const data = await response.json();

          if (!response.ok) {
            document.body.dispatchEvent(
              new CustomEvent('shapes:cart:adderror', {
                bubbles: true,
                detail: {
                  source: 'product-form',
                  sourceId: form.getAttribute('id') || '',
                  errors: data,
                },
              })
            );

            throw new Error(
              data?.description ||
              data?.message ||
              'Unable to add this item to the cart.'
            );
          }

          if (!data.sections) {
            throw new Error('Cart sections could not be refreshed.');
          }

          document.body.dispatchEvent(
            new CustomEvent('shapes:modalcart:afteradditem', {
              bubbles: true,
              detail: {
                response: data,
                forceOpen: true,
              },
            })
          );

          addedSuccessfully = true;
          if (buttonText) buttonText.textContent = addedText;

          window.setTimeout(() => {
            button.removeAttribute('aria-busy');
            this.updatePurchaseButtons();
          }, 900);
        } catch (error) {
          console.error('[FunnyProductPageV2] Add to cart error:', error);

          button.removeAttribute('aria-busy');
          if (buttonText) buttonText.textContent = normalText;
          this.updatePurchaseButtons();

          window.alert(
            error?.message ||
            'Unable to add this item to the cart.'
          );
        } finally {
          if (!addedSuccessfully) {
            button.removeAttribute('aria-busy');
            this.updatePurchaseButtons();
          }
        }
      });
    }

    destroy() {
      clearTimeout(this.viewingTimer);
      this.stickyVisibilityCleanup?.();
      this.stickyVisibilityCleanup = null;
    }
  }

  const init = scope => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : [...(scope.querySelectorAll?.(ROOT_SELECTOR) || [])];

    roots.forEach(root => {
      if (root.dataset.fppV2Ready === 'true') return;
      root.dataset.fppV2Ready = 'true';
      root._funnyProductPageV2 = new FunnyProductPageV2(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', event => init(event.target));

  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);

    root?._funnyProductPageV2?.destroy?.();
  });
})();
